import { useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  Eye
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: "id_card", label: "National ID Card", required: true },
  { value: "passport", label: "Passport", required: true },
  { value: "drivers_license", label: "Driver's License", required: true },
  { value: "job_letter", label: "Job Letter / Employment Letter", required: true },
  { value: "pay_slip", label: "Pay Slip / Salary Statement", required: true },
  { value: "bank_statement", label: "Bank Statement (Optional)", required: false },
];

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  verified: { label: "Verified", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function DocumentUpload({ 
  applicationId = null, 
  authHeader = {},
  onDocumentsChange = () => {},
  existingDocuments = []
}) {
  const [documents, setDocuments] = useState(existingDocuments);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedType) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a JPG, PNG, WebP, or PDF file");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", selectedType);
      if (applicationId) {
        formData.append("application_id", applicationId);
      }

      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...authHeader
        }
      });

      const newDoc = response.data;
      setDocuments([newDoc, ...documents]);
      onDocumentsChange([newDoc, ...documents]);
      
      toast.success(`${getDocumentLabel(selectedType)} uploaded successfully!`);
      setSelectedType("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const getDocumentLabel = (type) => {
    return DOCUMENT_TYPES.find(d => d.value === type)?.label || type;
  };

  const getUploadedTypes = () => {
    return documents.map(d => d.document_type);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="document-upload">
      <div>
        <Label className="text-base font-semibold">Upload Verification Documents</Label>
        <p className="text-sm text-gray-500 mt-1">
          Upload your documents for faster verification. ID + Job Letter + Pay Slip required.
        </p>
      </div>

      {/* Upload Section */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger data-testid="document-type-select">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((type) => {
                  const isUploaded = getUploadedTypes().includes(type.value);
                  return (
                    <SelectItem 
                      key={type.value} 
                      value={type.value}
                      disabled={isUploaded}
                    >
                      <span className="flex items-center gap-2">
                        {type.label}
                        {isUploaded && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        {!type.required && <span className="text-xs text-gray-400">(Optional)</span>}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={!selectedType || uploading}
              data-testid="document-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={!selectedType || uploading}
              className="btn-primary w-full sm:w-auto"
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Accepted formats: JPG, PNG, WebP, PDF (max 10MB)
        </p>
      </div>

      {/* Document Checklist */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Required Documents</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DOCUMENT_TYPES.filter(t => t.required).map((type) => {
            const isUploaded = getUploadedTypes().includes(type.value);
            const uploadedDoc = documents.find(d => d.document_type === type.value);
            
            return (
              <div
                key={type.value}
                className={`flex items-center gap-2 p-3 rounded-lg border ${
                  isUploaded 
                    ? "bg-green-50 border-green-200" 
                    : "bg-white border-gray-200"
                }`}
              >
                {isUploaded ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
                <span className={`text-sm ${isUploaded ? "text-green-700" : "text-gray-600"}`}>
                  {type.label.split(" / ")[0]}
                </span>
                {uploadedDoc && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${
                    STATUS_CONFIG[uploadedDoc.status]?.color || "bg-gray-100"
                  }`}>
                    {STATUS_CONFIG[uploadedDoc.status]?.label || uploadedDoc.status}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Uploaded Documents List */}
      {documents.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Uploaded Documents</Label>
          <div className="space-y-2">
            {documents.map((doc) => {
              const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200"
                  data-testid={`document-${doc.id}`}
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#0F3D3E] truncate">
                      {doc.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getDocumentLabel(doc.document_type)} • {formatFileSize(doc.file_size)}
                    </p>
                  </div>

                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </div>

                  {doc.extracted_data && doc.extracted_data.full_name && (
                    <div className="hidden sm:block text-xs text-gray-500">
                      Extracted: {doc.extracted_data.full_name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
